import React from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import { CapacityPlannerScreen } from '../../screen/CapacityPlanner/Screen';
import { projectColor, userColor } from './types';

interface Props { planner: CapacityPlannerScreen; }

function deadlineIcon(status: string): string {
    if (status === 'on-track') return '\u2713';
    if (status === 'at-risk') return '\u26a0';
    return '\u2717';
}

@observer
export class ProjectPanel extends React.Component<Props> {
    render() {
        const p = this.props.planner;
        if (!p.panelOpen || !p.selectedProjectId) return null;
        const proj = p.projects.find(pr => pr.id === p.selectedProjectId);
        if (!proj) return null;
        const allocs = p.getProjectAllocations(proj);
        const ms = p.getProjectMilestones(proj);
        const allTickets = p.getProjectTickets(proj);
        const totalHours = allTickets.reduce((s, t) => s + p.getTicketEstimate(t), 0);
        const deadline = p.getProjectDeadline(proj);
        const deadlineStatus = p.getDeadlineStatus(proj);
        return (
            <div className="panel" data-test-cp-project-panel>
                <div className="panel-header">
                    <h2>{proj.name}</h2>
                    <button className="btn-close" onClick={p.closePanel}>&times;</button>
                </div>
                <div className="panel-content">
                    <div className="panel-section">
                        <h3>Project Settings</h3>
                        <div className="form-group">
                            <label>Project Name</label>
                            <input type="text" defaultValue={proj.name} onChange={(e) => p.updateProjectName(e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label>Deadline (latest milestone)</label>
                            <input type="date" value={deadline ? moment(deadline).format('YYYY-MM-DD') : ''} readOnly />
                        </div>
                        <div className="form-group">
                            <label>Color</label>
                            <input type="color" value={projectColor(proj)} readOnly />
                        </div>
                    </div>
                    <div className="panel-section">
                        <h3>Team Allocation</h3>
                        {allocs.map(a => {
                            const u = p.users.find(ux => ux.id === (a as any).user?.id) as any;
                            if (!u) return null;
                            const days = p.getDaysPerWeek(a);
                            return (
                                <div key={a.id} className="allocation-item">
                                    <div className="dev-avatar" style={{ background: userColor(u) }}>{(u.fullName || '?').charAt(0)}</div>
                                    <div className="dev-info">
                                        <div className="dev-name">{u.fullName}</div>
                                        <div className="dev-days">{days} days/week</div>
                                    </div>
                                    <input type="range" className="allocation-slider" min="0" max="5" step="1" value={days}
                                        onChange={(e) => p.updateAllocDays(a, parseInt(e.target.value))} />
                                    <button className="btn-remove" onClick={() => p.removeAllocationFromPanel(a)}>&times;</button>
                                </div>
                            );
                        })}
                        <button className="btn btn-sm btn-secondary" onClick={() => p.openAllocationModal(proj.id as any)}>+ Add Team Member</button>
                    </div>
                    <div className="panel-section">
                        <h3>Milestones <span className="feature-summary">({ms.length} milestones, {allTickets.length} tickets, {totalHours}h)</span></h3>
                        {ms.map(m => {
                            const mAny = m as any;
                            const mTickets = p.getMilestoneTickets(mAny);
                            const mHours = mTickets.reduce((s, t) => s + p.getTicketEstimate(t), 0);
                            const doneCount = mTickets.filter(t => p.getTicketStatus(t) === 'done').length;
                            return (
                                <div key={m.id} className="milestone-panel-item">
                                    <div className="milestone-panel-header">
                                        <input type="text" className="milestone-name-input" defaultValue={m.name}
                                            onChange={(e) => p.updateMilestoneName(m.id as any, e.target.value)} />
                                        <span className="milestone-summary">{doneCount}/{mTickets.length} · {mHours}h</span>
                                        <button className="btn-remove" onClick={() => p.removeMilestone(m.id as any)}>&times;</button>
                                    </div>
                                    <div className="milestone-deadline-row">
                                        <label>Deadline:</label>
                                        <input type="date" value={mAny.dueAt ? moment(mAny.dueAt).format('YYYY-MM-DD') : ''}
                                            onChange={(e) => p.updateMilestoneDeadline(m.id as any, e.target.value)} />
                                    </div>
                                </div>
                            );
                        })}
                        <button className="btn btn-sm btn-secondary" style={{ width: '100%', marginTop: '0.5rem' }} onClick={() => p.addMilestoneToProject(proj.id as any)}>+ Add Milestone</button>
                    </div>
                    <div className="panel-section deadline-status">
                        {deadlineStatus ? (
                            <div className={'deadline-status ' + deadlineStatus.status}>
                                <span className="status-icon">{deadlineIcon(deadlineStatus.status)}</span>
                                {deadlineStatus.message}
                            </div>
                        ) : <div className="deadline-status">No deadline set</div>}
                    </div>
                    <div className="panel-actions">
                        <button className="btn btn-danger" onClick={p.deleteProject}>Delete Project</button>
                    </div>
                </div>
            </div>
        );
    }
}
